import State from "./State";
import GameView from "./view/GameView"
import FrameLogic from "./logic/FrameLogic"
import InputHandler from "./input/InputHandler";
import { Settings } from "./Settings";
import SkillTreeInputHandler from "./input/SkillTreeInputHandler";
import GameStateControl from "./GameStateControl.js";
import TimeFormatter from "./util/TimeFormatter.js";

export default class GameLoop {
    /**
     * @type {State} state
     * @type {GameView} gameView
     * @type {InputHandler} inputHandler
     * @param {SkillTreeInputHandler} skillTreeInputHandler
     */
    constructor(state, gameView, inputHandler, skillTreeInputHandler) {
        /**
         * @constant
         * @type {State}
         */
        this.state = state;
        /**
         * @constant
         * @type {GameView}
         */
        this.gameView = gameView;
        /**
         * @constant
         * @type {InputHandler}
         */
        this.inputHandler = inputHandler;
        /**
         * @constant
         * @type {SkillTreeInputHandler}
         */
        this.skillTreeInputHandler = skillTreeInputHandler;
        /**
         * @private
         * @type {number}
         */
        this.prevTimeStamp = performance.now();

        /**
         * @private
         * @constant
         * @type {FrameLogic}
         */
        this.frameLogic = new FrameLogic(inputHandler, skillTreeInputHandler);

        const selfReference = this;

        /**
         * @private
         * @type {function}
         */
        this.gameLoopReference = function () {
            selfReference.gameLoop();
        };

        /**
         * @private
         * @type {number}
         */
        this.fpsFrameCount = 0;
        /**
         * @private
         * @type {number}
         */
        this.fpsMillis = 0;

        /**
         * @constant
         * @private
         * @type {number}
         */
        this.offlineThresholdMillis = 1000 * 60 * 30; // 30 mins?

        /**
         * @type {GameStateControl}
         */
        this.gameStateControl = null;
        /**
         * @type {number}
         */
        this.callbackCode = -1;

        /**
         * @type {boolean}
         */
        this.prevFrameVisible = false;


        // 
        //////this.fpsIntervalMillis = 1000 / 150; // 30 is target fps
        //this.elapsedMillis = 0;
    }

    /**
     * 
     * @param {GameStateControl} gameStateControl 
     * @param {number} callbackCode
     */
    requestGameStateCallback(gameStateControl, callbackCode) {
        this.gameStateControl = gameStateControl;
        this.callbackCode = callbackCode;
    }

    startGameLoop() {

        this.fpsFrameCount = 0;
        this.fpsMillis = 0;
        this.prevTimeStamp = performance.now();

        window.requestAnimationFrame(this.gameLoopReference);
    }

    /**
     * Starts the game loop.
     * @private
     */
    gameLoop() {
        // Request that the game loop method be invoked as soon as the screen is ready to be repainted.
        window.requestAnimationFrame(this.gameLoopReference);

        const timeStamp = performance.now();
        const deltaMillis = timeStamp - this.prevTimeStamp;

        // FPS Throttling
        if (deltaMillis < this.state.gameOptions.getFpsMillis()) {
            return;
        }

        // Save the prev frame 'start' time stamp so that our FPS includes the time required to render the frame.
        this.prevTimeStamp = timeStamp;

        // Sometimes there are 0 millis between frames. We need to avoid a div/0 situation.
        const deltaTime = deltaMillis > 0 
            ? deltaMillis 
            : this.state.currentFps > 0 ? (1000 / this.state.currentFps) : (1000 / 60); // 1000m/fps = average elapsed millis per frame.


        // Offline detection
        if (!this.state.gameVisible || !this.prevFrameVisible) {
            const offlineSeconds = (deltaMillis / 1000) | 0;
            if (offlineSeconds > 0) {
                console.log('GameLoop.gameLoop() earned offline credit: ' + TimeFormatter.formatElapsedTime(offlineSeconds * 1000));
                this.state.totals.offlineSeconds += offlineSeconds;
            }

            // If we have just come back from being minimized, then let's do offline earnings.
            if (this.state.gameVisible && !this.prevFrameVisible && this.state.totals.offlineSeconds > 0) {
                this.state.offlineLogic.calculateOfflineEarnings();
            }
        }

        this.prevFrameVisible = this.state.gameVisible;

        // Check to see if we are doing a reload of the models and view.  If so, then 
        // instead of updating the frame logic and UI, we are going to load stuff.

        if (this.gameStateControl) {

            // Let the game reset object know that is has some work to do.
            this.gameStateControl.modelLoadCallback(this.callbackCode);

            // Clear out the call back reference.
            this.gameStateControl = null;
            this.callbackCode = 0;
        }
        else if (this.state.gameVisible && !this.state.gamePaused) {

            const frameTimeRatio = deltaTime / Settings.time.defaultMillisPerFrame;

            this.frameLogic.processFrameLogic(this.state, frameTimeRatio);

            this.gameView.updateView();
        }        

        // Reset the input for the next frame.
        this.inputHandler.resetInputHandler();
        this.skillTreeInputHandler.resetInputHandler();

        this.fpsFrameCount++;
        this.fpsMillis += deltaMillis;

        if (this.fpsFrameCount >= 60) {
            this.state.currentFps = (this.fpsFrameCount / (this.fpsMillis / 1000)) | 0;
            this.fpsFrameCount = 0;
            this.fpsMillis = 0;
        }

        // // Request that the game loop method be invoked as soon as the screen is ready to be repainted.
        // window.requestAnimationFrame(this.gameLoopReference);
    }
}